import { useCallback, useState } from 'react';
import _ from 'underscore';
import { useUser } from '@bingads-webui/overreact-cc';
import { usePromisify } from '@bingads-webui-react/overreact';
import {
  useCustomerLevelVariables,
  usePostponeReadVariables,
} from '@bingads-webui-campaign-react/basic-context-utils';

import { usePreference, preferenceNames } from './use-preference';
import { useUpdateNotificationSettings } from './use-update-notification-settings';

export const useLoadAndUpdateUserBusinessInfo = () => {
  const [{ value: phoneNumberCountryCode }, { update: updatePhoneNumberCountryCode }]
    = usePreference(preferenceNames.PhoneNumberCountryCode);
  const customerLevelVariables = useCustomerLevelVariables();
  const postponeReadConfig = usePostponeReadVariables();
  const [, { read: readUser, update: updateUser }] = useUser(customerLevelVariables, postponeReadConfig);
  const [readUserAsync, updateUserAsync] = usePromisify([readUser, updateUser]);
  const [userData, setUserData] = useState<Record<string, any>>({});
  const { updateNotificationSettings } = useUpdateNotificationSettings();

  const loadUserContactInfo = useCallback(() => readUserAsync({}).then((user) => {
    const userPhone = _.get(user, ['ContactInfo', 'Phone1'], '');
    const userEmail = _.get(user, ['Email'], '');
    const allowContact = _.get(user, ['AllowContact'], false);
    const allowContactByPhone = _.get(user, ['AllowContactByPhone'], false);

    setUserData(user);

    return {
      phone: userPhone,
      email: userEmail,
      phoneNumberCountryCode,
      allowContact,
      allowContactByPhone,
      isValid: true,
    };
  }), [phoneNumberCountryCode, readUserAsync]);

  // eslint-disable-next-line max-len
  const updateUserContactInfo = useCallback((contactInfo = {}, tc: Record<string, any> = {}, updateNotification = false) => {
    if (_.isEmpty(contactInfo) && _.isEmpty(tc)) {
      return Promise.resolve();
    }

    updatePhoneNumberCountryCode(contactInfo.phoneNumberCountryCode);

    let needUpdateNotification = updateNotification;
    // in AdsAppUI, use CommunicationPreferences to set AllowContact
    // in AdsAppUI, use ContactByPhone to set AllowContactByPhone
    const contactObj: Record<string, any> = {};
    if (_.has(tc, 'AllowContact') && _.get(userData, ['AllowContact'], false) !== tc.AllowContact) {
      needUpdateNotification = true;
      contactObj.CommunicationPreferences = tc.AllowContact ? 1 : 0;
    }
    if (_.has(tc, 'AllowContactByPhone') && _.get(userData, ['AllowContactByPhone'], false) !== tc.AllowContactByPhone) {
      needUpdateNotification = true;
      contactObj.ContactByPhone = tc.AllowContactByPhone;
    }

    const updateNotificationPromise =
      needUpdateNotification ? () => updateNotificationSettings(tc) : () => Promise.resolve();

    const updatedUserInfo = {
      ContactInfo: {
        ..._.get(userData, ['ContactInfo'], {}),
        ...(contactInfo.phone ? { Phone1: contactInfo.phone } : {}),
      },
      ...(contactInfo.email ? { Email: contactInfo.email } : {}),
    };

    if (_.get(userData, ['ContactInfo', 'Phone1'], '') === contactInfo.phone
      && _.get(userData, ['Email'], '') === contactInfo.email
      && !needUpdateNotification) {
      return Promise.resolve();
    }

    return updateUserAsync({
      ...updatedUserInfo,
      ...tc,
      ...contactObj,
    })
      .then(() => {
        setUserData(prevState => ({
          ...prevState,
          Email: contactInfo.email,
          ContactInfo: {
            ...prevState.ContactInfo,
            Phone1: contactInfo.phone,
          },
        }));

        return updateNotificationPromise();
      })
      .catch((error) => {
        throw error;
      });
  }, [updateNotificationSettings, updatePhoneNumberCountryCode, updateUserAsync, userData]);

  return {
    loadUserContactInfo,
    updateUserContactInfo,
  };
};
